// useCreatorStatus.ts
import { useGetCreatorStatusQuery } from "@/redux/feature/user/userApi";
import { TCreatorStatus } from "@/redux/feature/user/userType";

export const useCreatorStatus = (email: string) => {
  const {
    data,
    isLoading: queryLoading,
    isFetching,
    isError: isStatusError,
  } = useGetCreatorStatusQuery(encodeURIComponent(email) as string, {
    skip: !email,
  });

  const creatorStatus: TCreatorStatus = data?.data;

  const isLoading = !email || queryLoading || isFetching;
  const isError = !isLoading && isStatusError;

  // Shorthands (always optional-chain!)
  const app = creatorStatus?.applicationStatus;
  const asg = creatorStatus?.assignmentStatus;

  const applicationApproved = !!app?.isApproved;
  const applicationInitiated = !!app?.isInitiated;
  const applicationRejected = !!app?.isRejected;

  const assignmentInitiated = !!asg?.isInitiated;
  const assignmentApproved = !!asg?.isApproved;
  const assignmentRejected = !!asg?.isRejected;

  // Application states
  const isApplicationPending =
    applicationInitiated && !applicationApproved && !applicationRejected;

  const isNotInitiated =
    !applicationInitiated && !applicationApproved && !applicationRejected;

  const isApplicationRejected = applicationInitiated && applicationRejected;

  // Assignment states
  const isAssignmentPending =
    assignmentInitiated && !assignmentApproved && !assignmentRejected;

  const isAssignmentRejected = applicationApproved && assignmentRejected;

  // 🔑 Distinguish two “app approved, no assignment started” cases:
  // A) Awaiting assignment: popup should show
  const isAppApprovedAwaitingAssignment =
    applicationApproved && !assignmentInitiated && !assignmentApproved;

  // B) OVERWRITTEN: no popup (dashboard can show a card instead)
  const isOverwritten =
    applicationApproved && assignmentApproved && !assignmentInitiated;

  // (Keep your legacy name if other code consumes it, but update its meaning carefully.)
  // Here, keep it for backwards compatibility but align to A) only.
  const isCreatorApproved = isAppApprovedAwaitingAssignment;

  const rejectedReasons = app?.rejectedReason;

  // ✅ Popup should NOT include the overwritten case
  const shouldShowPopup =
    isApplicationPending ||
    isApplicationRejected ||
    isAssignmentPending ||
    isAssignmentRejected ||
    isAppApprovedAwaitingAssignment; // ← keep A), exclude isOverwritten

  const status = {
    isApplicationPending,
    isApplicationRejected,
    isAssignmentPending,
    isAssignmentRejected,
    isAppApprovedAwaitingAssignment, // new explicit flag
    isOverwritten, // new explicit flag
    isCreatorApproved, // legacy alias to A)
    rejectedReasons,
    isNotInitiated,
  };

  return {
    shouldShowPopup,
    status,
    isCreatorApproved, // (A)
    isOverwritten, // (B)
    rejectedReasons,
    isLoading,
    isError,
  };
};
